import type { LinkedStudy } from "@shared/types";

// State types -----------------------------------------------------------------

export type ReadStudyState =
  | { status: "fetching" }
  | { status: "success" }
  | { status: "error"; message: string };

export interface StudyState {
  id: string | null;
  study: LinkedStudy | null;
  readState: ReadStudyState;
}

export const initialStudyState: StudyState = {
  id: null,
  study: null,
  readState: { status: "fetching" },
};

// Actions ---------------------------------------------------------------------

export const ReadStudyAction = {
  Fetching: "READ_STUDY_FETCHING",
  Success: "READ_STUDY_SUCCESS",
  Error: "READ_STUDY_ERROR",
} as const;

export const ConfigStudyAction = {
  SetKey: "SET_STUDY_KEY",
} as const;

type Action<T extends string, P = object> = { type: T } & P;

export type StudyAction =
  | Action<typeof ReadStudyAction.Fetching>
  | Action<typeof ReadStudyAction.Success, { study: LinkedStudy }>
  | Action<typeof ReadStudyAction.Error, { message: string }>
  | Action<typeof ConfigStudyAction.SetKey, { id: string | null }>;

export type Dispatch = React.Dispatch<StudyAction>;

// Handlers --------------------------------------------------------------------

type Handlers = {
  [A in StudyAction as A["type"]]: (state: StudyState, action: A) => StudyState;
};

const handlers: Handlers = {
  [ReadStudyAction.Fetching]: (state) => ({
    ...state,
    readState: { status: "fetching" },
  }),
  [ReadStudyAction.Success]: (state, action) => ({
    ...state,
    readState: { status: "success" },
    study: action.study,
  }),
  [ReadStudyAction.Error]: (state, action) => ({
    ...state,
    readState: { status: "error", message: action.message },
  }),
  [ConfigStudyAction.SetKey]: (state, action) => ({
    ...state,
    id: action.id,
  }),
};

// Reducer ---------------------------------------------------------------------

export function studyReducer(state: StudyState, action: StudyAction): StudyState {
  const handler = handlers[action.type] as
    | ((s: StudyState, a: StudyAction) => StudyState)
    | undefined;
  return handler ? handler(state, action) : state;
}
